import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "utils/helpers/cn";

const buttonVariants = cva(
    `inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background
     transition-colors rounded-xl font-bold
     focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
     disabled:pointer-events-none disabled:opacity-50`,
    {
        variants: {
            variant: {
                default: `bg-f-secondary text-primary-foreground hover:bg-f-secondary/90 
                    dark:bg-foreground dark:text-background dark:hover:bg-foreground/70`,
                destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/70",
                outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
                secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                grey: "rounded-md border border-neutral-400 bg-neutral-500 text-primary-foreground  hover:bg-neutral-500/80",
                ghost: "hover:bg-accent hover:text-accent-foreground",
                link: "text-primary underline-offset-4 hover:underline",
                theme: `bg-theme-color text-theme-text-color hover:bg-theme-color/70 
                    dark:bg-foreground dark:text-background dark:hover:bg-foreground/70
                    transition-all duration-200`,
            },
            size: {
                default: "px-n8 py-n4",
                sm: " px-n3",
                md: "px-n4 py-n2",
                lg: "px-n8",
                icon: "h-n10 w-n10",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    dataTestId: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, dataTestId, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : "button";
        return (
            <Comp className={cn(buttonVariants({ className, variant, size }))} ref={ref} data-test-id={dataTestId} {...props} />
        );
    },
);
Button.displayName = "Button";

export { Button, buttonVariants };
